/** @format */
import { defineStore, type StoreDefinition } from 'pinia';
import { flat, commonRegExp } from '../utils/util';
import { INIT_FIELDS_ATTRIBUTE_TITLE_ENUM } from '../types';
import { desensitizeEnum } from './state';
import type {
	WidgetCPTType,
	BaseConfigVal,
	SetBaseConfig,
	Widget,
	Container,
	BasicField,
	WidgetConfig,
	WidgetConfigs,
	WidgetConfigType,
	FieldsType,
} from '../types';
import { store } from '@/store';

const DEFAULT_COL = '8';

// 是否为数字、字母、下划线、.
const REG_STR_NUM_POINT_ = /^[0-9a-zA-Z_.]{1,}$/;

const validateCode = (_: any, value: Required<WidgetConfigType>['code'], callback: any) => {
	const isDiffValue = REG_STR_NUM_POINT_.test(value);
	if (!isDiffValue) {
		callback(new Error('编码必须为数字字母下划线点类型'));
	} else {
		callback();
	}
};

const validateParams = (_: any, value: Required<WidgetConfigType>['params'], callback: any) => {
	if (!value || !value.length) {
		callback();
	}
	const isDiff = value.some((val) => {
		const isDiffValue = val.value.toString();
		const isDiffLabel = val.label.toString();
		if (isDiffValue && isDiffLabel) {
			return false;
		}
		return true;
	});

	const valueList = value.map((itm) => itm.value);
	const isEqualValue = new Set(valueList).size !== valueList.length;

	if (isEqualValue) {
		callback(new Error('字段编码不能重复'));
	} else if (isDiff) {
		callback(new Error('请完善字段信息，编码必须为数字字母下划线类型'));
	} else {
		callback();
	}
};

const validateCascaderParams = (_: any, value: Required<WidgetConfigType>['cascaderParams'], callback: any) => {
	if (!value || !value.length) {
		callback();
	}
	const flatArr = flat(value);

	const isDiff = flatArr.some((val) => {
		const isDiffValue = REG_STR_NUM_POINT_.test(val.value.toString());
		const isDiffLabel = val.label.toString();
		if (isDiffValue && isDiffLabel) {
			return false;
		}
		return true;
	});

	const valueList = flatArr.map((itm) => itm.value);
	const isEqualValue = new Set(valueList).size !== valueList.length;

	if (isEqualValue) {
		callback(new Error('字段编码不能重复'));
	} else if (isDiff) {
		callback(new Error('请完善字段信息，编码必须为数字字母下划线类型'));
	} else {
		callback();
	}
};

const validateLinkConfig = (_: any, value: Required<WidgetConfigType>['linkConfig'], callback: any) => {
	const valueList = value.map((itm) => itm.code);
	const isEqualValue = new Set(valueList).size !== valueList.length;

	const isDiff = value.some((val) => {
		if (val.code && val.link) {
			return false;
		}
		return true;
	});

	if (isEqualValue) {
		callback(new Error('关联对象不能重复'));
	} else if (isDiff) {
		callback(new Error('请完善关联对象数据'));
	} else {
		callback();
	}
};

const validateDesensitize = (_: any, value: Required<WidgetConfigType>['desensitize'], callback: any) => {
	switch (value.type) {
		case desensitizeEnum.custom:
			if (!value.regExp) callback(new Error('请完善自定义正则'));
			break;
		case desensitizeEnum.part:
			if (!(value.startNum && value.startNum > -1 && value.endNum && value.endNum > -1))
				callback(new Error('请完善起始结束位置'));
			break;

		default:
			break;
	}
	callback();
};

// --------------------- 组件配置 ---------------------

// 容器列表
const COMMON_CONTAINER = (): Widget<Container> => ({
	title: '容器控件',
	children: [
		{
			category: 'container',
			code: '',
			type: 'collapse',
			icon: 'Collection',
			children: [],
			pcProps: {
				label: '折叠面板',
				code: '',
				cols: '24',
				hidden: false,
				defaultOpen: true,
			},
		},
		{
			category: 'container',
			code: '',
			type: 'card',
			icon: 'Postcard',
			children: [],
			pcProps: {
				label: '卡片布局',
				code: '',
				cols: '24',
				hidden: false,
				isHiddenLabel: false,
				backgroundColor: '#fff',
				fontColor: '#000',
				labelColor: '#000',
			},
		},
		{
			category: 'container',
			code: '',
			type: 'tabs',
			icon: 'Build',
			children: [
				{
					category: 'container',
					type: 'tabs',
					code: '',
					pcProps: {
						label: 'tab1',
						cols: '24',
						code: '',
					},
					children: [],
					isActive: true,
				},
				{
					category: 'container',
					type: 'tabs',
					code: '',
					pcProps: {
						label: 'tab2',
						cols: '24',
						code: '',
					},
					children: [],
					isActive: false,
				},
			],
			pcProps: {
				label: '标签页',
				code: '',
				cols: '24',
				hidden: false,
				tabsParams: '',
				onTabChangeScript: '',
			},
		},
	],
});

// 基础字段列表
const COMMON_BASE_FIELDS = (): Widget<BasicField> => ({
	title: '基础控件',
	children: [
		{
			category: 'field',
			code: '',
			type: 'input',
			icon: 'Input',
			pcProps: {
				label: '单行文本',
				code: '',
				cols: DEFAULT_COL,
				hidden: false,
				isRequired: '1',
				defaultValue: '',
				maxlength: 50,
				isPassword: false,
				showPassword: false,
				placeholder: '',
				requiredMsg: '',
				validErrorMsg: '',
				validation: '',
				isHiddenLabel: false,
				onChangeScript: '',
				onClickScript: '',
				onDoubleClickScript: '',
				onBlurScript: '',
				onFoucsScript: '',
				labelWidth: undefined,
				showDisabledText: false,
				helpTips: '',
				desensitize: { type: undefined },
			},
		},
		{
			category: 'field',
			code: '',
			type: 'inputarea',
			icon: 'Textarea',
			pcProps: {
				label: '多行文本',
				code: '',
				cols: DEFAULT_COL,
				hidden: false,
				isRequired: '1',
				defaultValue: '',
				maxlength: 500,
				placeholder: '',
				requiredMsg: '',
				validErrorMsg: '',
				validation: '',
				rows: 1,
				isHiddenLabel: false,
				onChangeScript: '',
				onClickScript: '',
				onDoubleClickScript: '',
				onBlurScript: '',
				onFoucsScript: '',
				labelWidth: undefined,
				showDisabledText: false,
				helpTips: '',
				desensitize: { type: undefined },
			},
		},
		{
			category: 'field',
			code: '',
			type: 'input-number',
			icon: 'Number',
			pcProps: {
				label: '数字',
				code: '',
				cols: DEFAULT_COL,
				hidden: false,
				isRequired: '1',
				defaultValue: '',
				max: undefined,
				min: undefined,
				requiredMsg: '',
				precision: 0,
				step: 1,
				isHiddenLabel: false,
				onChangeScript: '',
				onClickScript: '',
				onDoubleClickScript: '',
				onBlurScript: '',
				onFoucsScript: '',
				labelWidth: undefined,
				showDisabledText: false,
				helpTips: '',
				desensitize: { type: undefined },
			},
		},
		{
			category: 'field',
			code: '',
			type: 'select',
			icon: 'Select',
			pcProps: {
				label: '下拉选',
				code: '',
				cols: DEFAULT_COL,
				hidden: false,
				isRequired: '1',
				multiple: false,
				placeholder: '',
				requiredMsg: '',
				allowCreate: false,
				multipleLimit: 0,
				isNotfilterable: false,
				isRemote: false,
				params: [],
				isHiddenLabel: false,
				onChangeScript: '',
				onClickScript: '',
				onRemoteScript: '',
				labelWidth: undefined,
				showDisabledText: false,
				helpTips: '',
			},
		},
		{
			category: 'field',
			code: '',
			type: 'radio',
			icon: 'Radio',
			pcProps: {
				label: '单选',
				code: '',
				cols: DEFAULT_COL,
				hidden: false,
				isRequired: '1',
				params: [],
				isButton: false,
				border: false,
				isTag: false,
				isHiddenLabel: false,
				onChangeScript: '',
				onClickScript: '',
				onDoubleClickScript: '',
				labelWidth: undefined,
				showDisabledText: false,
				helpTips: '',
			},
		},
		{
			category: 'field',
			code: '',
			type: 'checkbox',
			icon: 'Checkbox',
			pcProps: {
				label: '多选',
				code: '',
				cols: DEFAULT_COL,
				hidden: false,
				isRequired: '1',
				params: [],
				isButton: false,
				border: false,
				isTag: false,
				isHiddenLabel: false,
				onChangeScript: '',
				onClickScript: '',
				onDoubleClickScript: '',
				labelWidth: undefined,
				showDisabledText: false,
				helpTips: '',
			},
		},
		{
			category: 'field',
			code: '',
			type: 'cascader',
			icon: 'Cascader',
			pcProps: {
				label: '级联',
				code: '',
				cols: DEFAULT_COL,
				hidden: false,
				isRequired: '1',
				defaultValue: '',
				separator: '/',
				multiple: false,
				placeholder: '',
				requiredMsg: '',
				showAllLevels: true,
				checkStrictly: false,
				cascaderParams: [],
				isHiddenLabel: false,
				onChangeScript: '',
				labelWidth: undefined,
				showDisabledText: false,
				helpTips: '',
			},
		},
		{
			category: 'field',
			code: '',
			type: 'datetime',
			icon: 'Date',
			pcProps: {
				label: '日期时间',
				code: '',
				cols: DEFAULT_COL,
				hidden: false,
				isRequired: '1',
				defaultValue: '',
				placeholder: '',
				timeType: 'YYYY-MM-DD HH:mm:ss',
				isTimestamp: false,
				isTimeRange: false,
				isTenTimestamp: false,
				isHiddenLabel: false,
				onChangeScript: '',
				labelWidth: undefined,
				showDisabledText: false,
				helpTips: '',
				splitFlag: ',',
			},
		},
		{
			category: 'field',
			code: '',
			type: 'tree-select',
			icon: 'Cascader',
			pcProps: {
				label: '树形选择',
				code: '',
				cols: DEFAULT_COL,
				hidden: false,
				isRequired: '1',
				multiple: false,
				placeholder: '',
				requiredMsg: '',
				checkStrictly: false,
				params: [],
				isHiddenLabel: false,
				onChangeScript: '',
				labelWidth: undefined,
				showDisabledText: false,
				helpTips: '',
			},
		},
		{
			category: 'field',
			code: '',
			type: 'switch',
			icon: 'Switchcopy',
			pcProps: {
				label: '开关',
				code: '',
				cols: DEFAULT_COL,
				hidden: false,
				isRequired: '1',
				defaultValue: false,
				activeColor: '',
				inactiveColor: '',
				activeText: '',
				inactiveText: '',
				inlinePrompt: false,
				isHiddenLabel: false,
				onChangeScript: '',
				labelWidth: undefined,
				showDisabledText: false,
				helpTips: '',
			},
		},
		{
			category: 'field',
			code: '',
			type: 'rate',
			icon: 'Rate',
			pcProps: {
				label: '评分',
				code: '',
				cols: DEFAULT_COL,
				hidden: false,
				isRequired: '1',
				defaultValue: 0,
				requiredMsg: '',
				max: 5,
				allowHalf: false,
				showText: false,
				rateTexts: '',
				showScore: false,
				scoreTemplate: '{value}',
				isHiddenLabel: false,
				onChangeScript: '',
				labelWidth: undefined,
				showDisabledText: false,
				helpTips: '',
			},
		},
		{
			category: 'field',
			code: '',
			type: 'slider',
			icon: 'Slider',
			pcProps: {
				label: '滑块',
				code: '',
				cols: DEFAULT_COL,
				hidden: false,
				isRequired: '1',
				defaultValue: 0,
				max: 100,
				min: 0,
				step: 10,
				showStops: false,
				isHiddenLabel: false,
				onChangeScript: '',
				labelWidth: undefined,
				showDisabledText: false,
				helpTips: '',
			},
		},
		{
			category: 'other',
			code: '',
			type: 'placeholder',
			icon: 'Hide',
			pcProps: {
				cols: DEFAULT_COL,
				label: '占位符',
				code: '',
				isHiddenLabel: true,
			},
		},
	],
});

// 业务字段列表
const COMMON_BUSINESS_FIELDS = (): Widget<FieldsType> => ({
	title: '业务控件',
	children: [
		{
			category: 'field',
			code: '',
			type: 'user',
			icon: 'UserOutlined',
			pcProps: {
				label: '人员',
				code: '',
				cols: DEFAULT_COL,
				hidden: false,
				isRequired: '1',
				placeholder: '',
				requiredMsg: '',
				isHiddenLabel: false,
				onChangeScript: '',
				labelWidth: undefined,
				multiple: false,
				linkConfig: [],
				showDisabledText: false,
				isCurrentUser: false,
				userRange: '',
				userRangeDept: [],
				helpTips: '',
			},
		},
		{
			category: 'field',
			code: '',
			type: 'department',
			icon: 'UsergroupAddOutlined',
			pcProps: {
				label: '部门',
				code: '',
				cols: DEFAULT_COL,
				hidden: false,
				isRequired: '1',
				placeholder: '',
				requiredMsg: '',
				isHiddenLabel: false,
				onChangeScript: '',
				labelWidth: undefined,
				multiple: false,
				linkConfig: [],
				showDisabledText: false,
				isCurrentUser: false,
				helpTips: '',
			},
		},
		{
			category: 'container',
			code: '',
			type: 'subTable',
			icon: 'Table',
			children: [],
			pcProps: {
				label: '子表单',
				code: '',
				cols: '24',
				hidden: false,
				isHiddenLabel: false,
				actionButton: [],
				rowKey: 'id',
				showType: '0',
			},
		},
		// {
		// 	category: 'field',
		// 	code: '',
		// 	type: 'role',
		// 	icon: 'OfficeBuilding',
		// 	pcProps: {
		// 		label: '角色',
		// 		code: '',
		// 		cols: DEFAULT_COL,
		// 		hidden: false,
		// 		isRequired: '1',
		// 		placeholder: '',
		// 		requiredMsg: '',
		// 		isHiddenLabel: false,
		// 		onChangeScript: '',
		// 		labelWidth: undefined,
		// 		multiple: false,
		// 		linkConfig: [],
		// 	},
		// },
	],
});

// 其他字段列表
const COMMON_OTHER_FIELDS = (): Widget<BasicField> => ({
	title: '其他控件',
	children: [
		{
			category: 'other',
			code: '',
			type: 'button',
			icon: 'Button',
			pcProps: {
				cols: DEFAULT_COL,
				label: '按钮',
				code: '',
				hidden: false,
				isRequired: '1',
				buttonType: 'primary',
				position: 'left',
				isHiddenLabel: true,
				isLink: false,
				isRedirect: false,
				toUrl: '',
				onClickScript: '',
				labelWidth: undefined,
				backgroundColor: '',
				fontColor: '',
				borderRadius: undefined,
				height: '',
				width: '',
			},
		},
		{
			category: 'field',
			code: '',
			type: 'upload',
			icon: 'Uploadcopy',
			pcProps: {
				cols: '24',
				label: '上传附件',
				code: '',
				hidden: false,
				isRequired: '1',
				isHiddenLabel: true,
				accept: '',
				maxUpLength: undefined,
				showTip: false,
				maxSize: 0,
				labelWidth: undefined,
				templateFile: '',
				showTable: true,
				replaceFile: false,
				helpTips: '',
			},
		},
		{
			category: 'field',
			code: '',
			type: 'image',
			icon: 'ViewOutlined',
			pcProps: {
				cols: '24',
				label: '图片',
				code: '',
				hidden: false,
				isRequired: '1',
				isHiddenLabel: true,
				acceptImg: '',
				maxUpLength: 10,
				maxSize: 0,
				labelWidth: undefined,
				helpTips: '',
			},
		},
		{
			category: 'field',
			code: '',
			type: 'video',
			icon: 'VideoPlay',
			pcProps: {
				cols: '24',
				label: '视频',
				code: '',
				hidden: false,
				isRequired: '1',
				isHiddenLabel: true,
				acceptVideo: '',
				maxUpLength: 10,
				maxSize: 0,
				labelWidth: undefined,
				helpTips: '',
			},
		},
		{
			category: 'field',
			code: '',
			type: 'richText',
			icon: 'Log',
			pcProps: {
				cols: '24',
				label: '富文本',
				code: '',
				hidden: false,
				isRequired: '1',
				isHiddenLabel: true,
				labelWidth: undefined,
				helpTips: '',
			},
		},
		{
			category: 'other',
			code: '',
			type: 'iframe',
			icon: 'LayoutOutlined',
			pcProps: {
				cols: '24',
				label: 'iframe',
				code: '',
				hidden: false,
				isHiddenLabel: true,
				linkUrl: '',
				height: '500px',
				labelWidth: undefined,
			},
		},
		{
			category: 'field',
			code: '',
			type: 'staticText',
			icon: 'EditPen',
			pcProps: {
				cols: '24',
				label: '文本',
				code: '',
				hidden: false,
				isHiddenLabel: true,
				defaultValue: '',
				defaultValueType: 'inputarea',
				labelWidth: undefined,
				onClickScript: '',
				onDoubleClickScript: '',
				helpTips: '',
			},
		},
		{
			category: 'other',
			code: '',
			type: 'divider',
			icon: 'Minus',
			pcProps: {
				cols: '24',
				label: '分割线',
				code: '',
				hidden: false,
				isHiddenLabel: true,
				direction: 'horizontal',
			},
		},
		{
			category: 'other',
			code: '',
			type: 'esign',
			icon: 'CollectionTag',
			pcProps: {
				cols: DEFAULT_COL,
				label: '签名',
				code: '',
				hidden: false,
				isRequired: '1',
				isHiddenLabel: false,
				width: '100%',
				height: '300px',
				helpTips: '',
			},
		},
	],
});

const INIT_FIELDS = () => [COMMON_CONTAINER(), COMMON_BASE_FIELDS(), COMMON_BUSINESS_FIELDS(), COMMON_OTHER_FIELDS()];

// --------------------- 属性配置 ---------------------

// 字段属性配置
const INIT_FIELDS_ATTRIBUTE = (): WidgetConfigs => ({
	// ---------------------基础属性---------------------
	label: {
		type: 'input',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '字段名称',
		},
		formItemProps: {
			rules: [{ required: true, message: '字段名称为必填项', trigger: 'blur' }],
		},
	},
	code: {
		type: 'input',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '字段编码',
		},
		formItemProps: {
			rules: [
				{ required: true, message: '编码为必填项', trigger: 'blur' },
				{
					validator: validateCode,
					trigger: 'change',
				},
			],
		},
	},
	cols: {
		type: 'select',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '布局占比',
			params: [
				{ value: '2', label: '1/12栏' },
				{ value: '4', label: '1/6栏' },
				{ value: '6', label: '1/4栏' },
				{ value: '8', label: '1/3栏' },
				{ value: '12', label: '1/2栏' },
				{ value: '16', label: '2/3栏' },
				{ value: '18', label: '3/4栏' },
				{ value: '20', label: '5/6栏' },
				{ value: '22', label: '11/12栏' },
				{ value: '24', label: '1栏' },
			],
			isButton: true,
		},
	},
	isRequired: {
		type: 'radio',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '是否只读',
			params: [
				{ value: '0', label: '只读' },
				{ value: '1', label: '选填' },
				{ value: '2', label: '必填' },
			],
			isButton: true,
		},
	},
	hidden: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '是否隐藏',
		},
	},
	placeholder: {
		type: 'input',
		isBind: true,
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '占位内容',
		},
	},
	defaultValue: {
		type: 'defaultValue',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '默认值',
		},
	},
	helpTips: {
		type: 'input',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '提示信息',
		},
	},

	// ---------------------input 属性---------------------
	maxlength: {
		type: 'input-number',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		isBind: true,
		pcProps: {
			label: '最大长度',
		},
	},
	isPassword: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '是否为密码',
		},
	},
	showPassword: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		isBind: true,
		pcProps: {
			label: '展示密码图标',
		},
	},
	rows: {
		type: 'input-number',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		isBind: true,
		pcProps: {
			label: '行数',
			min: 1,
		},
	},

	// ---------------------number 属性---------------------
	max: {
		type: 'input-number',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.高级属性,
		isBind: true,
		pcProps: {
			label: '最大值',
		},
	},
	min: {
		type: 'input-number',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.高级属性,
		isBind: true,
		pcProps: {
			label: '最小值',
		},
	},
	precision: {
		type: 'input-number',
		isBind: true,
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.高级属性,
		pcProps: {
			label: '精度',
			min: 0,
			step: 1,
			stepStrictly: true,
		},
	},
	step: {
		type: 'input-number',
		isBind: true,
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.高级属性,
		pcProps: {
			label: '步长',
			min: 1,
		},
	},

	desensitize: {
		type: 'desensitize',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '脱敏',
		},
		formItemProps: {
			rules: [
				{
					validator: validateDesensitize,
					trigger: 'change',
				},
			],
		},
	},

	// ---------------------radio 属性---------------------
	isButton: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '显示为按钮',
		},
	},
	border: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '带有边框',
		},
	},
	isTag: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '显示为tag',
		},
	},

	// ---------------------select 属性---------------------
	multiple: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.选项设置,
		isBind: true,
		pcProps: {
			label: '是否多选',
		},
	},
	allowCreate: {
		type: 'switch',
		isBind: true,
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.选项设置,
		pcProps: {
			label: '允许创建选项',
		},
	},
	multipleLimit: {
		type: 'input-number',
		isBind: true,
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.选项设置,
		pcProps: {
			label: '多选数量限制',
			min: 0,
		},
	},
	isNotfilterable: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.选项设置,
		pcProps: {
			label: '取消搜索',
		},
	},
	isRemote: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.选项设置,
		pcProps: {
			label: '远程搜索',
		},
	},

	// ---------------------cascader 属性---------------------
	showAllLevels: {
		type: 'switch',
		isBind: true,
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.选项设置,
		pcProps: {
			label: '显示完整路径',
		},
	},
	checkStrictly: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.选项设置,
		pcProps: {
			label: '取消父子关联',
		},
	},
	separator: {
		type: 'input',
		isBind: true,
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.选项设置,
		pcProps: {
			label: '分隔符',
		},
	},

	// ---------------------datetime 属性---------------------
	timeType: {
		type: 'timeType',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '时间类型',
		},
	},
	isTimeRange: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '是否为时间区间',
		},
	},
	splitFlag: {
		type: 'input',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '时间区间分隔符',
		},
	},
	isTimestamp: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '是否为时间戳',
		},
	},
	isTenTimestamp: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '是否为10位时间戳',
		},
	},

	// ---------------------color 属性---------------------
	activeColor: {
		type: 'color',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.高级属性,
		pcProps: {
			label: '开启时背景色',
		},
	},
	inactiveColor: {
		type: 'color',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.高级属性,
		pcProps: {
			label: '关闭时背景色',
		},
	},
	activeText: {
		type: 'input',
		isBind: true,
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.高级属性,
		pcProps: {
			label: '关闭时文字描述',
		},
	},
	inactiveText: {
		type: 'input',
		isBind: true,
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.高级属性,
		pcProps: {
			label: '开启时文字描述',
		},
	},
	inlinePrompt: {
		type: 'switch',
		isBind: true,
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.高级属性,
		pcProps: {
			label: '图标显示在点内',
		},
	},

	// ---------------------rate 属性---------------------
	allowHalf: {
		type: 'switch',
		isBind: true,
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.高级属性,
		pcProps: {
			label: '允许半选',
		},
	},
	showScore: {
		type: 'switch',
		isBind: true,
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.高级属性,
		pcProps: {
			label: '显示当前分数',
		},
	},
	scoreTemplate: {
		type: 'input',
		isBind: true,
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.高级属性,
		pcProps: {
			label: '分数显示模板',
		},
	},
	showText: {
		type: 'switch',
		isBind: true,
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.高级属性,
		pcProps: {
			label: '显示辅助文字',
		},
	},
	rateTexts: {
		type: 'tags',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.高级属性,
		pcProps: {
			label: '辅助文字',
		},
	},

	// ---------------------slider 属性---------------------
	showStops: {
		type: 'switch',
		isBind: true,
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.高级属性,
		pcProps: {
			label: '显示断点',
		},
	},

	// ---------------------button 属性---------------------
	buttonType: {
		type: 'select',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '按钮类型',
			params: [
				{ value: 'primary', label: '主要' },
				{ value: 'success', label: '成功' },
				{ value: 'info', label: '信息' },
				{ value: 'warning', label: '警告' },
				{ value: 'danger', label: '危险' },
			],
		},
	},
	position: {
		type: 'radio',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '位置',
			params: [
				{ value: 'left', label: '居左' },
				{ value: 'center', label: '居中' },
				{ value: 'right', label: '居右' },
			],
			isButton: true,
		},
	},
	isLink: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '是否为链接',
		},
	},
	isRedirect: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '当前页面跳转',
		},
	},
	toUrl: {
		type: 'input',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '跳转地址',
		},
	},

	// ---------------------上传属性---------------------
	acceptImg: {
		type: 'select',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '上传文件类型',
			allowCreate: true,
			multiple: true,
			params: [
				{ value: 'image/jpg', label: '.jpg' },
				{ value: 'image/jpeg', label: '.jpeg' },
				{ value: 'image/jpe', label: '.jpe' },
				{ value: 'image/png', label: '.png' },
				{ value: 'image/bmp', label: '.bmp' },
			],
			helpTips: '为空则支持所有图片格式,支持自定义格式',
		},
	},
	acceptVideo: {
		type: 'select',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '上传文件类型',
			allowCreate: true,
			multiple: true,
			params: [
				{ value: 'video/mkv', label: '.mkv' },
				{ value: 'video/mp4', label: '.mp4' },
				{ value: 'video/avi', label: '.avi' },
				{ value: 'video/swf', label: '.swf' },
				{ value: 'video/wmv', label: '.wmv' },
				{ value: 'video/rmvb', label: '.rmvb' },
				{ value: 'video/mov', label: '.mov' },
				{ value: 'video/mpg', label: '.mpg' },
			],
			helpTips: '为空则支持所有视频格式,支持自定义格式',
		},
	},
	accept: {
		type: 'select',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '上传文件类型',
			allowCreate: true,
			multiple: true,
			params: [
				{ value: '.jpg', label: '.jpg' },
				{ value: '.jpeg', label: '.jpeg' },
				{ value: '.jpe', label: '.jpe' },
				{ value: '.png', label: '.png' },
				{ value: '.bmp', label: '.bmp' },
				{ value: '.txt', label: '.txt' },
				{ value: '.pdf', label: '.pdf' },
				{ value: '.doc', label: '.doc' },
				{ value: '.docx', label: '.docx' },
				{ value: '.xls', label: '.xls' },
				{ value: '.xlsx', label: '.xlsx' },
				{ value: '.ppt', label: '.ppt' },
				{ value: '.pptx', label: '.pptx' },
				{ value: '.zip', label: '.zip' },
				{ value: '.rar', label: '.rar' },
			],
			helpTips: '为空则所有格式都支持,支持自定义格式',
		},
	},
	maxUpLength: {
		type: 'input-number',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '最大上传数量',
			min: 0,
		},
	},
	showTip: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '上传提示',
		},
	},
	maxSize: {
		type: 'input-number',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '文件大小限制',
			min: 0,
		},
	},
	showTable: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '是否为表格展示',
		},
	},
	replaceFile: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '是否为单文件替换',
		},
	},
	// ---------------------iframe 属性---------------------
	linkUrl: {
		type: 'input',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '链接地址',
		},
		formItemProps: {
			rules: [{ required: true, message: '链接地址为必填项', trigger: 'blur' }],
		},
	},

	// ---------------------collapse 属性---------------------
	defaultOpen: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '默认展开',
		},
	},

	// ---------------------分割线 属性---------------------
	direction: {
		type: 'radio',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		isBind: true,
		pcProps: {
			label: '方向',
			params: [
				{ value: 'horizontal', label: '水平' },
				{ value: 'vertical', label: '竖直' },
			],
		},
	},
	templateFile: {
		type: 'upload',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '模板文件',
			showTable: false,
			showTip: false,
			maxUpLength: 1,
			accept: '',
		},
	},
	// ---------------------user 属性---------------------
	isCurrentUser: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.选项设置,
		pcProps: {
			label: '当前登录用户信息',
		},
	},
	userRange: {
		type: 'select',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.选项设置,
		pcProps: {
			label: '用户范围',
			params: [
				{ value: '1', label: '登录人所在部门人员' },
				{ value: '2', label: '登录人所在部门及其下级所有部门人员' },
				{ value: '3', label: '登录人的上级部门人员' },
				{ value: '4', label: '登录人的上级部门及其下级所有部门人员' },
				{ value: '5', label: '登录人的一级部门人员' },
				{ value: '6', label: '登录人的一级部门及其下级所有部门人员' },
				{ value: '7', label: '指定部门人员' },
				{ value: '8', label: '指定部门及其下级所有部门人员' },
			],
			allowCreate: true,
		},
	},
	userRangeDept: {
		type: 'userRangeDept',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.选项设置,
		pcProps: {
			label: '指定部门',
			multiple: true,
			helpTips: '用户范围为指定部门时,此配置才有效',
		},
	},
	// ---------------------params 属性---------------------
	linkConfig: {
		type: 'linkConfig',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.选项设置,
		pcProps: {
			label: '关联对象',
		},
		formItemProps: {
			rules: [
				{
					validator: validateLinkConfig,
					trigger: 'change',
				},
			],
			style: { display: 'block' },
		},
	},

	// ---------------------params 属性---------------------
	params: {
		type: 'params',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.选项设置,
		pcProps: {
			label: '下拉选项',
		},
		formItemProps: {
			rules: [
				{
					validator: validateParams,
					trigger: 'change',
				},
			],
			style: { display: 'block' },
		},
	},
	cascaderParams: {
		type: 'cascader-params',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.选项设置,
		pcProps: {
			label: '下拉选项',
		},
		formItemProps: {
			style: { display: 'block' },
			rules: [
				{
					validator: validateCascaderParams,
					trigger: 'change',
				},
			],
		},
	},
	tabsParams: {
		type: 'tabs-params',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.标签配置项,
		pcProps: {
			label: '标签配置项',
		},
		formItemProps: {
			style: { display: 'block' },
		},
	},

	// ---------------------校验 属性---------------------
	requiredMsg: {
		type: 'input',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.校验规则,
		pcProps: {
			label: '必填校验提示',
		},
	},
	validation: {
		type: 'select',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.校验规则,
		pcProps: {
			label: '字段校验',
			params: [
				{ value: commonRegExp.number, label: '数字' },
				{ value: commonRegExp.letter, label: '字母' },
				{ value: commonRegExp.letterAndNumber, label: '数字字母' },
				{ value: commonRegExp.mobilePhone, label: '手机号码' },
				{ value: commonRegExp.idCard, label: '身份证' },
				{ value: commonRegExp.ip, label: 'ip' },
				{ value: commonRegExp.email, label: '邮箱' },
				{ value: commonRegExp.url, label: '网址' },
				{ value: commonRegExp.noChinese, label: '非中文字符' },
				{ value: commonRegExp.chinese, label: '仅中文字符' },
			],
			allowCreate: true,
		},
	},
	validErrorMsg: {
		type: 'input',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.校验规则,
		pcProps: {
			label: '校验失败提示',
		},
	},

	// ---------------------样式 属性---------------------
	isHiddenLabel: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.样式属性,
		pcProps: {
			label: '隐藏标签',
		},
	},
	labelWidth: {
		type: 'input-number',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.样式属性,
		pcProps: {
			label: '标签宽度',
			defaultValue: undefined,
			min: 50,
		},
	},
	size: {
		type: 'radio',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.样式属性,
		pcProps: {
			label: '尺寸',
			params: [
				{ value: 'large', label: '较大' },
				{ value: 'default', label: '默认' },
				{ value: 'small', label: '迷你' },
			],
			isButton: true,
		},
	},
	showDisabledText: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.样式属性,
		pcProps: {
			label: '只读展示文本',
		},
	},
	width: {
		type: 'input',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.样式属性,
		pcProps: {
			label: '宽度',
		},
	},
	height: {
		type: 'input',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.样式属性,
		pcProps: {
			label: '高度',
		},
	},
	backgroundColor: {
		type: 'color',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.样式属性,
		pcProps: {
			label: '背景色',
		},
	},
	fontColor: {
		type: 'color',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.样式属性,
		pcProps: {
			label: '字体颜色',
		},
	},
	labelColor: {
		type: 'color',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.样式属性,
		pcProps: {
			label: 'label颜色',
		},
	},
	borderRadius: {
		type: 'slider',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.样式属性,
		pcProps: {
			label: '圆角度数',
			max: 45,
		},
	},
	// ---------------------事件 属性---------------------
	onChangeScript: {
		type: 'script',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.事件,
		pcProps: {
			label: '值变化事件',
		},
	},
	onClickScript: {
		type: 'script',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.事件,
		pcProps: {
			label: '单击事件',
		},
	},
	onRemoteScript: {
		type: 'script',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.事件,
		pcProps: {
			label: '远程搜索事件',
		},
	},
	onDoubleClickScript: {
		type: 'script',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.事件,
		pcProps: {
			label: '双击事件',
		},
	},
	onBlurScript: {
		type: 'script',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.事件,
		pcProps: {
			label: '失焦事件',
		},
	},
	onFoucsScript: {
		type: 'script',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.事件,
		pcProps: {
			label: '聚焦事件',
		},
	},
	onTabChangeScript: {
		type: 'script',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.事件,
		pcProps: {
			label: '标签切换事件',
		},
	},

	// 子表单
	rowKey: {
		type: 'input',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '行唯一值',
		},
		formItemProps: {
			rules: [{ required: true, message: '行唯一值为必填项', trigger: 'blur' }],
		},
	},

	actionButton: {
		type: 'actionButton',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.功能按钮,
		pcProps: {},
		formItemProps: {
			style: { display: 'block' },
		},
	},
	showType: {
		type: 'select',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '展现形式',
			params: [
				{ value: '0', label: '列表' },
				{ value: '1', label: '表单' },
			],
		},
	},
});

// 表单属性配置
const INIT_FORM_ATTRIBUTE = (): Record<string, WidgetConfig> => ({
	labelPosition: {
		type: 'radio',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '标签对齐',
			params: [
				{ value: 'left', label: '左对齐' },
				{ value: 'top', label: '顶部' },
				{ value: 'right', label: '右对齐' },
			],
			defaultValue: 'right',
			isButton: true,
		},
	},
	labelWidth: {
		type: 'input-number',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '标签宽度',
			defaultValue: undefined,
			min: 50,
		},
	},
	size: {
		type: 'radio',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '尺寸',
			params: [
				{ value: 'large', label: '较大' },
				{ value: 'default', label: '默认' },
				{ value: 'small', label: '迷你' },
			],
			defaultValue: 'default',
			isButton: true,
		},
	},
	labelEllipsis: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '标签溢出省略',
			defaultValue: false,
		},
	},
	hasAnchor: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '是否展示目录',
			defaultValue: false,
		},
	},
	showDisabledText: {
		type: 'switch',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.常见属性,
		pcProps: {
			label: '只读展示文本',
			defaultValue: false,
		},
	},
	onLoadScript: {
		type: 'script',
		parentTitle: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM.事件,
		pcProps: {
			label: '初始化事件',
		},
	},
});

// 不展示默认值属性的字段
const INIT_DONT_SHOW_DEFAULT_FIELDS = (): WidgetCPTType[] => ['checkbox', 'radio', 'select'];

// 手机端不展示的属性
const INIT_DONT_SHOW_MOBILE_FIELDS = (): [keyof WidgetConfigType] => ['cols'];

// 提供给外部可以调整基础属性的数据
const useBaseConfigStore = (name?: string): StoreDefinition =>
	defineStore({
		id: name ? `${name}-baseConfig` : 'pc-form-designer-baseConfig',

		state: (): BaseConfigVal => ({
			// 组件列表
			FIELDS: INIT_FIELDS(),
			// 组件属性对象
			FIELDS_ATTRIBUTE: INIT_FIELDS_ATTRIBUTE(),
			// 表单属性对象
			FORM_ATTRIBUTE: INIT_FORM_ATTRIBUTE(),
			// 不展示默认值的字段
			DONT_SHOW_DEFAULT_FIELDS: INIT_DONT_SHOW_DEFAULT_FIELDS(),
			// 在组件设置中分组展示
			FIELDS_ATTRIBUTE_TITLE_ENUM: INIT_FIELDS_ATTRIBUTE_TITLE_ENUM,
			// 字段渲染组件列表
			FIELDS_COMPONENTS: {},
			// 手机端不展示的属性
			DONT_SHOW_MOBILE_FIELDS: INIT_DONT_SHOW_MOBILE_FIELDS(),
		}),

		actions: {
			// 修改基础属性
			async setState(func: SetBaseConfig) {
				const {
					FIELDS,
					FIELDS_ATTRIBUTE,
					FORM_ATTRIBUTE,
					DONT_SHOW_DEFAULT_FIELDS,
					FIELDS_ATTRIBUTE_TITLE_ENUM,
					FIELDS_COMPONENTS,
					DONT_SHOW_MOBILE_FIELDS,
				} = await func({
					FIELDS: this.FIELDS,
					FIELDS_ATTRIBUTE: this.FIELDS_ATTRIBUTE,
					FORM_ATTRIBUTE: this.FORM_ATTRIBUTE,
					DONT_SHOW_DEFAULT_FIELDS: this.DONT_SHOW_DEFAULT_FIELDS,
					FIELDS_ATTRIBUTE_TITLE_ENUM: this.FIELDS_ATTRIBUTE_TITLE_ENUM,
					DONT_SHOW_MOBILE_FIELDS: this.DONT_SHOW_MOBILE_FIELDS,
				});
				FIELDS && (this.FIELDS = FIELDS);
				FIELDS_ATTRIBUTE && (this.FIELDS_ATTRIBUTE = FIELDS_ATTRIBUTE);
				FORM_ATTRIBUTE && (this.FORM_ATTRIBUTE = FORM_ATTRIBUTE);
				DONT_SHOW_DEFAULT_FIELDS && (this.DONT_SHOW_DEFAULT_FIELDS = DONT_SHOW_DEFAULT_FIELDS);
				FIELDS_ATTRIBUTE_TITLE_ENUM && (this.FIELDS_ATTRIBUTE_TITLE_ENUM = FIELDS_ATTRIBUTE_TITLE_ENUM);
				FIELDS_COMPONENTS && (this.FIELDS_COMPONENTS = FIELDS_COMPONENTS);
				DONT_SHOW_MOBILE_FIELDS && (this.DONT_SHOW_MOBILE_FIELDS = DONT_SHOW_MOBILE_FIELDS);
			},

			// 获取直接绑定到组件自身的属性
			getFieldsBindpcProps() {
				return Object.entries(this.FIELDS_ATTRIBUTE).reduce((arr, [key, value]) => {
					return value.isBind ? [...arr, key] : arr;
				}, []);
			},

			// 右侧组件设置字段排版
			getFieldsAttributeWidget() {
				return Object.entries(this.FIELDS_ATTRIBUTE_TITLE_ENUM).reduce((arr, [key, value]) => {
					if (parseInt(key, 10) >= 0) {
						return [
							...arr,
							{
								title: value as string,
								props: Object.entries(this.FIELDS_ATTRIBUTE).reduce((ar, [k, v]) => {
									if (v.parentTitle === +key) {
										return [...ar, k];
									} else {
										return ar;
									}
								}, []),
							},
						];
					}
					return arr;
				}, []);
			},

			// 表单属性字段排版
			getFormAttributeWidget() {
				return Object.entries(this.FIELDS_ATTRIBUTE_TITLE_ENUM).reduce((arr, [key, value]) => {
					if (parseInt(key, 10) >= 0) {
						const props = Object.entries(this.FORM_ATTRIBUTE).reduce((ar, [k, v]) => {
							if (v.parentTitle === +key) {
								return [...ar, k];
							} else {
								return ar;
							}
						}, []);
						if (props.length) {
							return [
								...arr,
								{
									title: value as string,
									props,
								},
							];
						} else {
							return arr;
						}
					}
					return arr;
				}, []);
			},

			// 获取表单属性默认值
			getFormAttributeDefaultValue() {
				return Object.entries(this.FORM_ATTRIBUTE).reduce((obj, [key, value]) => {
					if (Object.prototype.hasOwnProperty.call(value.pcProps, 'defaultValue')) {
						obj[key] = value.pcProps.defaultValue;
					}
					return obj;
				}, {});
			},

			// 集合属性列表提供给script脚本自动填充
			getScriptSnippets() {
				return Object.keys(this.FIELDS_ATTRIBUTE).map((i) => ({
					meta: `组件属性-${this.FIELDS_ATTRIBUTE[i].pcProps.label}`,
					caption: i,
					value: i,
					score: 1,
				}));
			},
		},
	});

export function useBaseConfigStoreWithOut(name?: string): any {
	return useBaseConfigStore(name)(store);
}
